import { useCallback } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFormContext } from '../contexts/FormContext';
import { useContactSubmission } from '../hooks/useContactSubmission';
import { useCarfaxSubmission } from '../hooks/useCarfaxSubmission';
import { useLeadPushing } from '../hooks/useLeadPushing';
import { useAnalyticsTracking } from '../hooks/useAnalyticsTracking';
import { useChatMessages } from '../hooks/useChatMessages';
import { pushADFLead } from '../utils/adfLeadPush';
import { getStoredInterestData } from '../utils/interestDataUtils';
import { logger } from '../utils/logger';
import SecureStorage from '../utils/secureStorage';
import { sessionManager } from '../utils/sessionManager';
import { supabase } from '../integrations/supabase/client';
import { getDealerID } from '../utils/dealerDataUtils';
import { getFormEmailsFromPage } from '../utils/adfEmailDecryption';
import { createFormSubmission, formatFormSubmissionForChat } from '../utils/formSubmissionUtils';
import { extractFormContext, mergeWithExistingInterest } from '../utils/formContextExtraction';

// Helper to get conversation from sessionStorage (where messages are saved synchronously)
const getConversationFromStorage = (): { id: string; sender: 'user' | 'assistant'; content: string; timestamp: string }[] => {
  try {
    // Primary: sessionStorage
    let messagesRaw = sessionStorage.getItem('d2c_chat_messages');
    
    // Fallback: localStorage backup
    if (!messagesRaw) {
      messagesRaw = localStorage.getItem('d2c_chat_messages_backup');
    }
    
    if (!messagesRaw) {
      logger.log('📝 [FORM] No conversation found in storage');
      return [];
    }
    
    const messages = JSON.parse(messagesRaw);
    if (!Array.isArray(messages)) return [];
    
    // Convert to SessionMessage format
    const formatted = messages.map((msg: { role: string; content: string }, index: number) => ({
      id: `msg_${index}_${Date.now()}`,
      sender: msg.role === 'user' ? 'user' as const : 'assistant' as const,
      content: msg.content || '',
      timestamp: new Date().toISOString()
    }));
    
    logger.log(`📝 [FORM] Retrieved ${formatted.length} messages from storage for AI summary`);
    return formatted;
  } catch (error) {
    console.error('📝 [FORM] Error reading conversation from storage:', error);
    return [];
  }
};

// Hook wrapper for the service
export const useFormSubmissionService = (
  sendMessage: (message: string, formData?: any) => Promise<void>
) => {
  const { t, language } = useLanguage();
  const { activeForm, closeForm } = useFormContext();
  const { processContactSubmission } = useContactSubmission();
  const { processCarfaxSubmission } = useCarfaxSubmission();
  const { pushLead } = useLeadPushing();
  const { trackLeadSent } = useAnalyticsTracking();
  const { addAIMessage } = useChatMessages();

  // PII storage functionality removed

  const recordFormSubmission = useCallback(async (formTypeName: string) => {
    const submittedForms = (await SecureStorage.getItem('submittedFormsInSession')) || [];
    if (!submittedForms.includes(formTypeName)) {
      submittedForms.push(formTypeName);
      await SecureStorage.setItem('submittedFormsInSession', submittedForms);
    }
    
    // Record timestamp to prevent duplicate Form objects from OpenAI
    await SecureStorage.setItem('lastFormSubmissionTime', Date.now());
    logger.log('⏰ Recorded form submission timestamp for duplicate prevention');
  }, []);

  // Helper function to extract and use form emails for targeted lead submission (memoized)
  const processFormEmails = useCallback(async (formType: string) => {
    try {
      const result = await getFormEmailsFromPage();
      
      // Handle any extraction failures gracefully (invalid tokens, missing tokens, etc.)
      if (!result.success) {
        logger.log('⚠️ Form emails extraction failed, continuing without:', result.error);
        return;
      }
      
      if (result.success && result.form_emails) {
        // 🚨 TRIGGER DEBUG MODAL - Show extracted emails
        window.dispatchEvent(new CustomEvent('showEmailsDebug', {
          detail: {
            formEmails: result.form_emails,
            dealerId: result.dealer_id,
            domain: window.location.hostname,
            success: result.success
          }
        }));
        
        // Determine target department based on form type, AI department, and vehicle data
        let targetDepartment = 'New'; // default
        const formTypeLower = formType.toLowerCase();
        
        // Check AI department for service intent first (highest priority for service-related)
        const interestData = await getStoredInterestData();
        const aiDepartment = interestData?.department?.toLowerCase();
        const isServiceIntent = formTypeLower.includes('service') || aiDepartment === 'service' || aiDepartment === 'parts';
        
        if (isServiceIntent) {
          targetDepartment = 'Service';
          logger.log(`🔧 Service intent detected (formType: ${formType}, AI department: ${interestData?.department}) - routing to Service (non-overridable)`);
        } else if (formTypeLower.includes('finance')) {
          targetDepartment = 'Finance';
        } else if (formTypeLower.includes('used')) {
          targetDepartment = 'Used';
        } else {
          // Check if we should auto-determine based on vehicle mileage first, then year
          if (interestData?.vehicle) {
            // Check mileage first (most reliable indicator)
            if (interestData.vehicle.mileage !== undefined && interestData.vehicle.mileage !== null) {
              const mileage = typeof interestData.vehicle.mileage === 'string' ? 
                parseInt(interestData.vehicle.mileage) : interestData.vehicle.mileage;
              
              if (mileage > 200) {
                targetDepartment = 'Used';
                logger.log(`🎯 Auto-routing to Used department based on vehicle mileage: ${mileage}km (>200km)`);
              } else {
                logger.log(`🎯 Keeping New department based on vehicle mileage: ${mileage}km (≤200km)`);
              }
            } else if (interestData.vehicle.year) {
              // Fallback to year if mileage not available
              const currentYear = new Date().getFullYear();
              const vehicleYear = parseInt(interestData.vehicle.year.toString());
              
              if (vehicleYear < currentYear) {
                targetDepartment = 'Used';
                logger.log(`🎯 Auto-routing to Used department based on vehicle year: ${vehicleYear} (current: ${currentYear}) - mileage not available`);
              }
            }
          }
        }
        
        // Find the appropriate department email
        const departmentEmail = result.form_emails.find(email => email.Department === targetDepartment);
        if (departmentEmail) {
          return {
            htmlEmail: departmentEmail.HTML_email,
            adfEmail: departmentEmail.ADF_email,
            department: targetDepartment
          };
        }
      }
    } catch (error) {
      logger.log('⚠️ FORM_EMAILS_EXTRACTION_FAILED:', error);
    }
    
    // FALLBACK: Since no form emails are configured for this dealer,
    // create mock department-specific emails based on the form type and vehicle data
    let targetDepartment = 'New'; // default
    const formTypeLower = formType.toLowerCase();
    
    // Check AI department for service intent (highest priority for service-related)
    let fallbackInterestData: any = null;
    try {
      fallbackInterestData = await getStoredInterestData();
    } catch (error) {
      logger.log('⚠️ Failed to get stored interest data:', error);
    }
    const fallbackAiDept = fallbackInterestData?.department?.toLowerCase();
    const fallbackIsService = formTypeLower.includes('service') || fallbackAiDept === 'service' || fallbackAiDept === 'parts';
    
    if (fallbackIsService) {
      targetDepartment = 'Service';
      logger.log(`🔧 Service intent detected (formType: ${formType}, AI department: ${fallbackInterestData?.department}) - routing to Service (non-overridable)`);
    } else if (formTypeLower.includes('finance')) {
      targetDepartment = 'Finance';
      logger.log('🎯 Routing to Finance department for form type:', formType);
    } else if (formTypeLower.includes('used')) {
      targetDepartment = 'Used';
      logger.log('🎯 Routing to Used department for form type:', formType);
    } else {
      // Check if we should auto-determine based on vehicle mileage first, then year (fallback logic)
      if (fallbackInterestData?.vehicle) {
        // Check mileage first (most reliable indicator)
        if (fallbackInterestData.vehicle.mileage !== undefined && fallbackInterestData.vehicle.mileage !== null) {
          const mileage = typeof fallbackInterestData.vehicle.mileage === 'string' ? 
            parseInt(fallbackInterestData.vehicle.mileage) : fallbackInterestData.vehicle.mileage;
          
          if (mileage > 200) {
            targetDepartment = 'Used';
            logger.log(`🎯 Auto-routing to Used department based on vehicle mileage: ${mileage}km (>200km)`);
          } else {
            logger.log(`🎯 Keeping New department based on vehicle mileage: ${mileage}km (≤200km)`);
          }
        } else if (fallbackInterestData.vehicle.year) {
          // Fallback to year if mileage not available
          const currentYear = new Date().getFullYear();
          const vehicleYear = parseInt(fallbackInterestData.vehicle.year.toString());
          
          if (vehicleYear < currentYear) {
            targetDepartment = 'Used';
            logger.log(`🎯 Auto-routing to Used department based on vehicle year: ${vehicleYear} (current: ${currentYear})`);
          }
        }
      }
      
      // Default to New department
      logger.log('🎯 Defaulting to New department for form type:', formType);
    }
    
    // Return undefined emails if none configured - caller must handle
    logger.log('⚠️ No form emails configured for dealer, ADF lead will only send if dealer has send_adf_leads_to configured');
    return {
      htmlEmail: undefined,
      adfEmail: undefined,
      department: targetDepartment
    };
  }, []);

  // Helper function to push ADF leads for all form types (memoized)
  const pushADFFormLead = useCallback(async (
    submittedFormData: { [key: string]: string },
    formType: string,
    contactData: any,
    interestData: any,
    formEmailsData: any = null
  ) => {
    try {
      // Get current conversation for chat transcription (last 10 messages only for current thread)
      const session = sessionManager.getCurrentSession();
      const allMessages = session?.messages || [];
      const conversation = allMessages.slice(-10); // Only include last 10 messages for current thread

      // Map interest data to ADF vehicle format
      const vehicles = [];
      
      // Determine vehicle interest based on form type AND AI department - CRITICAL for CRM routing
      // Service intent from AI (Service, Parts) always takes priority and cannot be overridden
      const departmentFromAI = interestData?.department?.toLowerCase();
      const isServiceForm = formType.toLowerCase() === 'service' || departmentFromAI === 'service' || departmentFromAI === 'parts';
      const vehicleInterest = isServiceForm ? 'service' : 'buy';
      
      if (isServiceForm && formType.toLowerCase() !== 'service') {
        logger.log(`🔧 AI detected service intent (department: ${interestData?.department}) - overriding to Service regardless of vehicle data`);
      }
      
      // PRIORITY 1: Add vehicle from form submission data first
      if (submittedFormData.Make || submittedFormData.make || submittedFormData.Year || submittedFormData.year) {
        
        // For service forms, status should be 'service'; otherwise use Interest.department from AI
        let vehicleStatus: 'new' | 'used' | 'service' = isServiceForm ? 'service' : 'new';
        
        // Only calculate new/used status for non-service forms
        if (!isServiceForm) {
          // PRIORITY 1: Check specific vehicle data (mileage/year) - this is the ACTUAL vehicle being submitted
          // This takes priority over AI department because AI department may be from a previous query about different vehicles
          const formMileage = submittedFormData.Mileage || submittedFormData.mileage;
          const formYear = submittedFormData.Year || submittedFormData.year;
          
          let determinedFromVehicleData = false;
          
          if (formMileage) {
            const mileageNum = parseFloat(formMileage.toString().replace(/[^\d.]/g, ''));
            if (mileageNum > 200) {
              vehicleStatus = 'used';
              determinedFromVehicleData = true;
              logger.log(`🎯 ADF Vehicle classified as USED based on form mileage: ${formMileage} -> ${mileageNum}km`);
            } else {
              vehicleStatus = 'new';
              determinedFromVehicleData = true;
              logger.log(`🎯 ADF Vehicle classified as NEW based on form mileage: ${formMileage} -> ${mileageNum}km (≤200km)`);
            }
          } else if (formYear) {
            const currentYear = new Date().getFullYear();
            const vehicleYear = parseInt(formYear.toString());
            if (vehicleYear >= currentYear) {
              vehicleStatus = 'new';
              determinedFromVehicleData = true;
              logger.log(`🎯 ADF Vehicle classified as NEW based on form year: ${vehicleYear} (current: ${currentYear})`);
            } else {
              vehicleStatus = 'used';
              determinedFromVehicleData = true;
              logger.log(`🎯 ADF Vehicle classified as USED based on form year: ${vehicleYear} (current: ${currentYear})`);
            }
          }
          
          // PRIORITY 2: Fall back to AI department only if vehicle data didn't determine status
          if (!determinedFromVehicleData) {
            if (departmentFromAI === 'used') {
              vehicleStatus = 'used';
              logger.log(`🎯 ADF Vehicle status set to USED based on Interest.department from AI (no vehicle data available)`);
            } else if (departmentFromAI === 'new') {
              vehicleStatus = 'new';
              logger.log(`🎯 ADF Vehicle status set to NEW based on Interest.department from AI (no vehicle data available)`);
            } else {
              logger.log(`🎯 ADF Vehicle defaulting to NEW (no vehicle data or AI department available)`);
            }
          }
        } else {
          logger.log(`🎯 Service form detected - setting vehicle interest="${vehicleInterest}" status="${vehicleStatus}" for CRM routing`);
        }
        
        vehicles.push({
          interest: vehicleInterest as 'buy' | 'service',
          status: vehicleStatus,
          id: submittedFormData['Stock #'] || submittedFormData.stock,
          year: submittedFormData.Year || submittedFormData.year,
          vehicletype: 'Car',
          make: submittedFormData.Make || submittedFormData.make,
          model: submittedFormData.Model || submittedFormData.model,
          trim: submittedFormData.Trim || submittedFormData.trim,
          vin: submittedFormData.VIN || submittedFormData.vin,
          stock: submittedFormData['Stock #'] || submittedFormData.stock,
          price: submittedFormData['vehicle.price'] || submittedFormData.price,
          mileage: submittedFormData.Mileage || submittedFormData.mileage,
          color: submittedFormData.Color || submittedFormData['Exterior Color'] || submittedFormData.color,
          exterior_color: submittedFormData['Exterior Color'] || submittedFormData.Color || submittedFormData.exterior_color,
          fuel_type: submittedFormData['Fuel Type'] || submittedFormData.fuel_type
        });
        
        logger.log(`🎯 Added vehicle from form submission with interest: ${vehicleInterest}, status: ${vehicleStatus}`);
      }
      // PRIORITY 2: Fallback to interest data if no form vehicle data
      else if (interestData?.vehicle) {
        const vehicle = interestData.vehicle;
        
        // For service forms, status should be 'service'; otherwise determine from mileage/year
        let vehicleStatus: 'new' | 'used' | 'service' = isServiceForm ? 'service' : 'new';
        
        logger.log(`🔍 ADF Vehicle Status Debug - Raw vehicle data:`, JSON.stringify(vehicle, null, 2));
        logger.log(`🔍 ADF Vehicle Status Debug - Form type:`, formType, 'isServiceForm:', isServiceForm);
        
        // Only calculate new/used status for non-service forms
        if (!isServiceForm) {
          // PRIORITY 1: Check specific vehicle data (mileage/year) - this is the ACTUAL vehicle being submitted
          // This takes priority over AI department because AI department may be from a previous query about different vehicles
          let determinedFromVehicleData = false;
          
          if (vehicle.mileage !== undefined && vehicle.mileage !== null) {
            let mileage: number;
            
            if (typeof vehicle.mileage === 'string') {
              mileage = parseFloat(vehicle.mileage.replace(/[^\d.]/g, ''));
              logger.log(`🔍 ADF Vehicle Status - Parsed string mileage "${vehicle.mileage}" to ${mileage}km`);
            } else {
              mileage = vehicle.mileage;
              logger.log(`🔍 ADF Vehicle Status - Using numeric mileage: ${mileage}km`);
            }
            
            if (mileage > 200) {
              vehicleStatus = 'used';
              determinedFromVehicleData = true;
              logger.log(`🎯 ADF Vehicle classified as USED based on mileage: ${mileage}km (>200km)`);
            } else {
              vehicleStatus = 'new';
              determinedFromVehicleData = true;
              logger.log(`🎯 ADF Vehicle classified as NEW based on mileage: ${mileage}km (≤200km)`);
            }
          } else if (vehicle.year) {
            const currentYear = new Date().getFullYear();
            const vehicleYear = parseInt(vehicle.year.toString());
            
            logger.log(`🔍 ADF Vehicle Status - No mileage, checking year: ${vehicleYear} vs current ${currentYear}`);
            
            if (vehicleYear >= currentYear) {
              vehicleStatus = 'new';
              determinedFromVehicleData = true;
              logger.log(`🎯 ADF Vehicle classified as NEW based on year: ${vehicleYear} (current: ${currentYear})`);
            } else {
              vehicleStatus = 'used';
              determinedFromVehicleData = true;
              logger.log(`🎯 ADF Vehicle classified as USED based on year: ${vehicleYear} (current: ${currentYear})`);
            }
          }
          
          // PRIORITY 2: Fall back to AI department only if vehicle data didn't determine status
          if (!determinedFromVehicleData) {
            if (departmentFromAI === 'used') {
              vehicleStatus = 'used';
              logger.log(`🎯 ADF Vehicle status set to USED based on Interest.department from AI (no vehicle data available)`);
            } else if (departmentFromAI === 'new') {
              vehicleStatus = 'new';
              logger.log(`🎯 ADF Vehicle status set to NEW based on Interest.department from AI (no vehicle data available)`);
            } else {
              logger.log(`🎯 ADF Vehicle defaulting to NEW (no vehicle data or AI department available)`);
            }
          }
        } else {
          logger.log(`🎯 Service form detected - setting vehicle interest="${vehicleInterest}" status="${vehicleStatus}" for CRM routing`);
        }
        
        vehicles.push({
          interest: vehicleInterest as 'buy' | 'service',
          status: vehicleStatus,
          id: vehicle.stock || vehicle.vin,
          year: vehicle.year,
          vehicletype: 'Car', // Default to Car, could be enhanced based on vehicle data
          make: vehicle.make,
          model: vehicle.model,
          trim: vehicle.version || vehicle.trim,
          vin: vehicle.vin || vehicle.VIN,
          stock: vehicle.stock,
          price: vehicle.price,
          mileage: vehicle.mileage,
          color: vehicle.color || vehicle.exterior_color,
          exterior_color: vehicle.exterior_color || vehicle.color,
          interior_color: vehicle.interior_color,
          fuel_type: vehicle.fuel_type,
          engine: vehicle.engine,
          transmission: vehicle.transmission,
          features: vehicle.features,
          description: vehicle.description,
          main_picture: vehicle.main_picture,
          carfax_url: vehicle.carfax_url,
          vdp_url: vehicle.vdp_url
        });
      }

      // Add trade-in vehicle if form has trade-in data
      if (submittedFormData.trade_year || submittedFormData.tradeYear) {
        vehicles.push({
          interest: 'trade-in' as const,
          status: 'used' as const,
          year: submittedFormData.trade_year || submittedFormData.tradeYear,
          make: submittedFormData.trade_make || submittedFormData.tradeMake,
          model: submittedFormData.trade_model || submittedFormData.tradeModel,
          trim: submittedFormData.trade_trim || submittedFormData.tradeTrim,
          condition: 'used' as const,
          odometer: submittedFormData.trade_mileage || submittedFormData.tradeMileage ? {
            value: submittedFormData.trade_mileage || submittedFormData.tradeMileage,
            status: 'original' as const,
            units: 'km' as const
          } : undefined
        });
      }

      // Get session ID for ADF using async method
      const currentSession = await sessionManager.getCurrentSessionAsync();
      const sessionId = currentSession?.sessionId;

      // Prepare ADF lead parameters
      
      // Get conversation summary from session
      const conversationSummary = currentSession?.conversationSummary;
      
      const adfParams = {
        prospect: {
          id: sessionId || `session_${Date.now()}`, // Fallback to timestamp-based ID
          language: interestData?.language || 'English'
        },
        vehicles: vehicles.length > 0 ? vehicles : undefined,
        customer: {
          name: contactData.Name || 'Unknown Customer',
          phone: contactData.Phone,
          email: contactData.Email,
          address: {
            postalcode: submittedFormData.postalCode || submittedFormData.postal_code,
            country: 'CA'
          }
        },
        comments: submittedFormData.message || submittedFormData.comments,
        formSubmission: submittedFormData,
        interestData: interestData, // Pass interest data for comments
        chatTranscription: conversation,
        formType: formType,
        formEmailsData: formEmailsData, // Pass the extracted emails
        conversationSummary: conversationSummary // Pass conversation summary for appending to comments
      };
      logger.log('🟢 STEP 6 - Final adfParams.interestData:', JSON.stringify(adfParams.interestData, null, 2));

      const adfResult = await pushADFLead(adfParams);
      return adfResult.leadId;
    } catch (error) {
      logger.log('💥 ADF_ERROR:', error);
    }
  }, []);

  const sendCarfaxSms = useCallback(async (
    submittedFormData: { [key: string]: string },
    interestData: any
  ): Promise<boolean> => {
    const phone = submittedFormData.phone || submittedFormData.Phone;
    const name = submittedFormData.name || submittedFormData.Name || '';
    if (!phone) return false;

    const currentSession = await sessionManager.getCurrentSessionAsync();
    const sessionId = currentSession?.sessionId;
    const dealerId = await getDealerID();
    const vehicle = interestData?.vehicle || {};
    const vin = submittedFormData.vin || vehicle.vin || vehicle.VIN || '';
    const carfaxUrl = submittedFormData.carfaxUrl || vehicle.carfax_url || vehicle.CARFAX_URL || (vin ? `https://www.carfax.com/VehicleHistory/p/Report.cfx?partner=CFL_0&vin=${vin}` : '');

    if (!sessionId || !dealerId || !carfaxUrl) {
      logger.warn('⚠️ CARFAX SMS skipped - missing required data', { hasSessionId: !!sessionId, dealerId, hasCarfaxUrl: !!carfaxUrl });
      return false;
    }

    const { data: grantData, error: grantError } = await supabase.functions.invoke('issue-sms-grant', {
      body: { sessionId, dealerId }
    });

    if (grantError || !grantData?.success || !grantData?.token) {
      console.error('❌ CARFAX SMS grant failed:', grantError || grantData);
      return false;
    }

    const vehicleLabel = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim || vehicle.version]
      .filter(Boolean)
      .join(' ');
    const message = language === 'fr'
      ? `Voici votre rapport Carfax${vehicleLabel ? ` pour ${vehicleLabel}` : ''}: ${carfaxUrl}`
      : `Here is your Carfax report${vehicleLabel ? ` for ${vehicleLabel}` : ''}: ${carfaxUrl}`;

    const { data, error } = await supabase.functions.invoke('send-sms-through-d2c', {
      body: {
        toNumber: phone,
        message,
        dealerId: String(dealerId),
        sessionId,
        smsToken: grantData.token,
        lang: language === 'fr' ? 'fr' : 'en',
        name,
        make: vehicle.make || '',
        model: vehicle.model || '',
        stockNumber: vehicle.stock || '',
        year: vehicle.year ? String(vehicle.year) : '',
        vin,
        attach: carfaxUrl,
        leadType: 'D2C_Chatbot_SMS'
      }
    });

    if (error || !data?.success) {
      console.error('❌ CARFAX SMS send failed:', error || data);
      return false;
    }

    logger.log('✅ CARFAX SMS sent successfully:', data);
    return true;
  }, [language]);

  const handleCarfaxFlow = useCallback(async (submittedFormData: { [key: string]: string }) => {
    const { submission, messageToSend } = await processCarfaxSubmission(submittedFormData);
    
    // Also push ADF lead for Carfax requests
    const { contactData } = await processContactSubmission(submittedFormData);
    const storedInterestData = await getStoredInterestData();
    
    if (contactData.Email || contactData.Phone) {
      // PII storage removed
      
      // Extract form emails for Carfax
      const formEmailsData = await processFormEmails('Carfax');
      
      // Extract context from conversation for Carfax leads too
      // Use sessionStorage where messages are saved synchronously
      const conversation = getConversationFromStorage();
      const extractedContext = await extractFormContext(conversation, 'Carfax', submittedFormData);
      const interestData = mergeWithExistingInterest(extractedContext, storedInterestData);
      
      const leadId = await pushADFFormLead(submittedFormData, 'Carfax', contactData, interestData, formEmailsData);
      const smsSent = await sendCarfaxSms(submittedFormData, interestData);
      // Track lead submission
      trackLeadSent('carfax', { formType: 'Carfax', ...contactData });

      recordFormSubmission('Carfax');
      closeForm(true);
      if (smsSent) {
        toast.success(language === 'fr' ? '\u2713 Demande Carfax re\u00e7ue; SMS en cours d\'envoi.' : '\u2713 Carfax request received; SMS is being sent.');
      } else {
        toast.error(language === 'fr' ? "La demande a \u00e9t\u00e9 re\u00e7ue, mais le SMS n'a pas pu \u00eatre envoy\u00e9." : 'Request received, but the SMS could not be sent.');
      }

      const thankYouMessage = smsSent
        ? (language === 'fr'
          ? "Merci! Votre demande Carfax a \u00e9t\u00e9 re\u00e7ue; le SMS est en cours d'envoi."
          : 'Thanks! Your Carfax request was received; the SMS is being sent.')
        : (language === 'fr'
          ? "Merci! Votre demande Carfax a \u00e9t\u00e9 re\u00e7ue, mais le SMS n'a pas pu \u00eatre envoy\u00e9."
          : 'Thanks! Your Carfax request was received, but the SMS could not be sent.');
      setTimeout(async () => {
        await addAIMessage(thankYouMessage);
      }, 500);

      return leadId;
    }
    
    recordFormSubmission('Carfax');
    closeForm();
    toast.success(t('form.toast.success.carfax'));
    
    // COMMENTED OUT: Automatic OpenAI API calls disabled to prevent unwanted VDP context messages
    // GUARD: Only send if we have actual message content
    // if (messageToSend && messageToSend.trim()) {
    //   logger.log('✅ FORM SUBMISSION: Sending valid form message to OpenAI');
    //   await sendMessage(messageToSend, submission);
    // } else {
    //   console.error('🚫 BLOCKED: Empty form submission message');
    // }
    
    // Send automatic thank you message from AI for Carfax
    const thankYouMessage = language === 'fr' 
      ? "Merci! Un sp&eacute;cialiste vous contactera sous peu!" 
      : "Thanks! A specialist will contact you shortly!";
    
    // Add a small delay to make it feel more natural
    setTimeout(async () => {
      await addAIMessage(thankYouMessage);
    }, 500);
  }, [processCarfaxSubmission, processContactSubmission, pushADFFormLead, sendCarfaxSms, recordFormSubmission, closeForm, t, sendMessage, processFormEmails, trackLeadSent, language, addAIMessage]);

  const handleRegularFormFlow = useCallback(async (submittedFormData: { [key: string]: string }) => {
    const { contactData, interestData: initialInterestData, sourceData, formType, hasValidContact } = await processContactSubmission(submittedFormData);
    logger.log('🟢 STEP 2 - After processContactSubmission, interestData:', JSON.stringify(initialInterestData, null, 2));
    logger.log('🟢 STEP 2 - interestData.message:', initialInterestData?.message);

    if (hasValidContact) {
      // PII storage removed
      
      // FIRST: Extract form emails to determine target emails
      const formEmailsData = await processFormEmails(formType);
      
      // NEW: Extract form context from conversation using OpenAI (only on form submission)
      // This replaces the dependency on Interest/Contact objects in the AI's JSON schema
      // CRITICAL: Get conversation from sessionStorage where messages are saved synchronously
      // sessionManager.getCurrentSession() may be stale/empty due to async timing issues
      const conversation = getConversationFromStorage();
      
      logger.log('📝 [FORM] Extracting context from conversation with form data...');
      const extractedContext = await extractFormContext(conversation, formType, submittedFormData);
      
      // Merge extracted context with existing interest data (prioritize user-clicked vehicle data)
      let interestData = mergeWithExistingInterest(extractedContext, initialInterestData);
      
      // CRITICAL: Update interestData with the determined department before sending to ADF
      if (formEmailsData?.department) {
        logger.log(`🚨 SETTING DEPARTMENT IN INTEREST DATA: ${formEmailsData.department}`);
        interestData.department = formEmailsData.department;
      }
      
      // Push to existing D2C system  
      await pushLead(contactData, interestData, submittedFormData, formType);
      
      // HTML staff notification removed: D2C CRM handles notifications after
      // the REST push (D2C_ChatLead_Post). No need to duplicate.


      
      // Also push ADF lead (this will use the correct department-specific emails)
      const leadId = await pushADFFormLead(submittedFormData, formType, contactData, interestData, formEmailsData);
      
      // Track lead submission
      trackLeadSent(formType, { formType, ...contactData });
      return leadId;
    }

    recordFormSubmission(activeForm || 'Contact');
    closeForm();
    
    const toastFormType = activeForm?.toLowerCase() || 'default';
    toast.success(t(`form.toast.success.${toastFormType}`) || t('form.toast.success.default'));

    // Send the form submission message for display
    const submissionMessage = (submittedFormData as any).message || `FORM_SUBMISSION:${JSON.stringify({
      type: 'form_submission',
      formData: submittedFormData,
      formType: formType
    })}`;
    
    logger.log('📤 FormSubmissionService: Sending form submission message:', submissionMessage);
    logger.log('📤 FormSubmissionService: Active form:', activeForm);
    
    // COMMENTED OUT: Automatic OpenAI API calls disabled to prevent unwanted VDP context messages
    // GUARD: Only send if we have actual message content
    // if (submissionMessage && submissionMessage.trim()) {
    //   logger.log('✅ FORM SUBMISSION: Sending valid submission message to OpenAI');
    //   await sendMessage(submissionMessage);
    // } else {
    //   console.error('🚫 BLOCKED: Empty submission message');
    // }
    
    // Send automatic thank you message from AI
    const thankYouMessage = language === 'fr' 
      ? "Merci! Un sp&eacute;cialiste vous contactera sous peu!" 
      : "Thanks! A specialist will contact you shortly!";
    
    // Add a small delay to make it feel more natural
    setTimeout(async () => {
      await addAIMessage(thankYouMessage);
    }, 500);
  }, [activeForm, processContactSubmission, pushLead, pushADFFormLead, recordFormSubmission, closeForm, t, sendMessage, processFormEmails, trackLeadSent]);

  const handleFormSubmit = useCallback(async (submittedFormData: { [key: string]: string }): Promise<string | undefined> => {
    try {
      let leadId: string | undefined;
      if (activeForm === 'Carfax') {
        logger.log('🚀 ROUTING TO CARFAX FLOW');
        leadId = await handleCarfaxFlow(submittedFormData);
      } else {
        logger.log('🚀 ROUTING TO REGULAR FLOW');
        leadId = await handleRegularFormFlow(submittedFormData);
      }
      logger.log('🚀 FORM SUBMIT COMPLETED, leadId:', leadId);
      return leadId;
    } catch (error) {
      console.error('💥 FORM_ERROR:', error);
      toast.error(t('error.form.submission.failed'));
      return undefined;
    }
  }, [activeForm, handleCarfaxFlow, handleRegularFormFlow, t]);

  return {
    handleFormSubmit,
    processFormEmails
  };
};